import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Button, PageHeader, Select, Spin, Form, Alert } from 'antd'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import InputForm from './components/inputForm'
import TextAreaForm from './components/textAreaForm'
import { errorMessage, successMessage } from 'services/messager'
import {
	createDemand,
	getOptions,
	getSolicitantAreasOptions,
} from 'services/demands/demandsServices'
import Uploader from 'components/uploadFile'
import FormSection from './components/FormSection'
import { mimeTypes } from 'utils/file'
import { priorities } from 'utils/constants'

import { StyledForm, StyledFormItem } from './styles'
import styles from './index.module.scss'
import FormFactory from 'components/formFactory'
import useTemplateVarsForm from './hooks/useTemplateVarsForm'
import { appendAnswer } from 'states/modules/answer'
import { nextPage, previousPage } from 'states/modules/question'

const CreateDemand = () => {
	const history = useHistory()
	const queryClient = useQueryClient()
	const dispatch = useDispatch()
	const [templateVariablesForm] = Form.useForm()

	const user = useSelector(({ session }) => session)
	const answer = useSelector(({ answer }) => answer)

	const { control, handleSubmit, watch, setValue, reset, trigger, getValues } =
		useForm({
			defaultValues: {
				taskName: '',
				description: '',
				categoryId: undefined,
				templateId: undefined,
				priority: undefined,
				fileList: [],
				solicitantAreaId: undefined,
			},
		})

	const [currentStep, setCurrentStep] = useState(0)
	const [moduleTooltip, setModuleTooltip] = useState('')
	const [requireTemplateFields, setRequireTemplateFields] = useState(false)
	const [isUploadTemplate, setIsUploadTemplate] = useState(false)

	const taskNameLabel =
		user.company_id === 96 ? 'Título do chamado' : 'Fornecedor/Contraparte'
	const isAttachmentRequired = user.company_id === 96

	const fileList = watch('fileList')
	const currentCategory = watch('categoryId')
	const currentTemplate = watch('templateId')
	const templateId = watch('templateId')

	const { loading: isLoadingTemplateVarsForm } = useTemplateVarsForm(templateId)

	const fetchCategories = async () => {
		try {
			const data = await getOptions({
				route: '/categories/list',
				page: 1,
				limit: 100,
			})
			return data.list
		} catch (error) {
			errorMessage({ content: 'Erro ao obter categorias' })
		}
	}

	const fetchModels = async () => {
		try {
			const data = await getOptions({
				route: '/templates/list',
				page: 1,
				limit: 100,
			})
			return data.list
		} catch (error) {
			errorMessage({ content: 'Erro ao obter modelos' })
		}
	}

	const { data: categories, isLoading: isLoadingCategories } = useQuery({
		queryKey: ['category-options'],
		queryFn: fetchCategories,
	})

	const { data: models, isLoading: isLoadingModels } = useQuery({
		queryKey: ['models-options'],
		queryFn: fetchModels,
	})

	const { data: solicitantAreas, isLoading: isLoadingAreas } = useQuery({
		queryKey: ['solicitant-areas-options'],
		queryFn: () => getSolicitantAreasOptions(''),
	})

	const { mutate: newDemand, isPending: isLoadingDemand } = useMutation({
		mutationFn: async (values) => {
			const formData = new FormData()

			Object.entries(values).forEach(([key, value]) => {
				if (Array.isArray(value) && value[0] instanceof File) {
					value.forEach((file) => formData.append('files', file))
				} else {
					formData.append(key, value)
				}
			})

			return await createDemand(formData)
		},
		onSuccess: () => {
			successMessage({ content: 'Nova demanda cadastrada com sucesso!' })
			reset()
			queryClient.invalidateQueries({ queryKey: ['demands'] })
			history.push('/demands')
		},
		onError: () => {
			errorMessage({ content: 'Erro ao cadastrar nova demanda.' })
		},
	})

	const onSubmit = (values) => newDemand(values)

	const handleGoBack = () => {
		reset()
		history.push('/demands')
	}

	const getSelectOptions = (options) =>
		options?.map((option) => (
			<Select.Option
				key={option.id || option.value}
				value={option.id || option.value}>
				{option.name || option.label}
			</Select.Option>
		))

	const onChangeCategory = (value, onChangeField) => {
		const found = categories?.find((c) => c.id === value)
		setRequireTemplateFields(found?.require_template)
		onChangeField(value)
	}

	const onChangeTemplate = (value, onChangeField) => {
		const found = models?.find((t) => t.id === value)
		setIsUploadTemplate(found?.text_type === 'upload')
		onChangeField(value)
	}

	const handleNextStep = async () => {
		const isValid = await trigger()

		if (!isValid) return

		if (requireTemplateFields && !isUploadTemplate) {
			setCurrentStep(1)
			return
		}

		handleSubmit(onSubmit)()
	}

	const hasPreview = false

	const { questions, lastPage } = useSelector(
		({ question }) => ({
			questions: question.data,
			lastPage: hasPreview ? question.lastPage + 1 : question.lastPage,
		}),
		shallowEqual
	)

	const visible = useSelector(({ question }) => question.visible)
	const currentPage = useSelector(({ question }) => question.currentPage)
	const isLastPage = currentPage === lastPage

	const handleBackNavigation = () => {
		if (currentStep === 1) {
			if (currentPage > 0) {
				dispatch(previousPage())
			} else {
				setCurrentStep(0)
			}
		}
	}

	const onNext = async () => {
		try {
			await templateVariablesForm.validateFields()

			dispatch(
				appendAnswer({
					data: templateVariablesForm.getFieldsValue(),
					pageFieldsData: questions[currentPage],
					visible: visible[currentPage],
				})
			)

			if (!isLastPage) {
				dispatch(nextPage({ hasPreview }))
			} else {
				handleSubmit((values) =>
					onSubmit({
						...values,
						variables: JSON.stringify(answer.data),
					})
				)()
			}
		} catch (e) {}
	}

	useEffect(() => {
		if (!models || !currentTemplate) return
		const template = models.find((m) => m.id === currentTemplate)
		setModuleTooltip(template?.config?.description || '')
	}, [models, currentTemplate])

	return (
		<div className={styles.layout}>
			<PageHeader
				title="Nova Demanda"
				onBack={handleGoBack}
				className={styles.header}
			/>

			<div className={styles.content}>
				<StyledForm layout="vertical">
					{currentStep === 0 && (
						<div className={styles.formSection}>
							<FormSection
								title="Informações da demanda"
								description="Preencha cuidadosamente as informações da demanda
para que o responsável jurídico tenha um entendimento claro sobre
a solicitação."
							/>

							<StyledFormItem>
								<p className={styles.label}>Área Solicitante</p>
								<Controller
									name="solicitantAreaId"
									control={control}
									rules={{ required: 'Selecione uma área solicitante' }}
									render={({ field, fieldState }) => (
										<>
											<Select
												{...field}
												placeholder="Selecione a área solicitante"
												onChange={field.onChange}
												allowClear
												disabled={isLoadingAreas}
												notFoundContent={
													isLoadingAreas ? <Spin size="small" /> : null
												}>
												{getSelectOptions(solicitantAreas)}
											</Select>

											{fieldState.error && (
												<span className={styles.errorMessage}>
													{fieldState.error.message}
												</span>
											)}
										</>
									)}
								/>
							</StyledFormItem>

							<InputForm
								name="taskName"
								control={control}
								label={taskNameLabel}
								required
							/>

							<TextAreaForm
								name="description"
								control={control}
								label="Descrição"
								required
							/>

							<StyledFormItem required>
								<p className={styles.label}>Categoria</p>
								<Controller
									name="categoryId"
									control={control}
									rules={{ required: 'Selecione uma categoria' }}
									render={({ field }) => (
										<Select
											{...field}
											onChange={(v) => onChangeCategory(v, field.onChange)}
											disabled={isLoadingCategories}>
											{getSelectOptions(categories)}
										</Select>
									)}
								/>
							</StyledFormItem>

							{requireTemplateFields && (
								<>
									<StyledFormItem required>
										<p className={styles.label}>Modelo</p>
										<Controller
											name="templateId"
											control={control}
											rules={{ required: 'Selecione um modelo' }}
											render={({ field }) => (
												<Select
													{...field}
													onChange={(v) => onChangeTemplate(v, field.onChange)}
													disabled={isLoadingModels}>
													{getSelectOptions(models)}
												</Select>
											)}
										/>
									</StyledFormItem>

									{moduleTooltip && (
										<Alert
											type="info"
											showIcon
											message="Informações do modelo"
											description={moduleTooltip}
											style={{ whiteSpace: 'pre-line' }}
										/>
									)}
								</>
							)}

							<StyledFormItem required>
								<p className={styles.label}>Prioridade</p>
								<Controller
									name="priority"
									control={control}
									rules={{ required: 'Selecione a prioridade' }}
									render={({ field }) => (
										<Select {...field} onChange={field.onChange}>
											{getSelectOptions(priorities)}
										</Select>
									)}
								/>
							</StyledFormItem>

							<StyledFormItem required={isAttachmentRequired}>
								<p className={styles.label}>Anexos</p>

								<Uploader
									multiple
									max={5}
									fileList={fileList}
									setFileList={(files) => setValue('fileList', files)}
									allowedTypes={Object.keys(mimeTypes)}
									allowedExtensions={Object.values(mimeTypes)}
								/>
								<p className={styles.additionalInfo}>
									* Limite total: <strong>125 MB</strong>
								</p>
							</StyledFormItem>
						</div>
					)}

					{currentStep === 1 && templateId && !isUploadTemplate && (
						<div className={styles.formSection}>
							<FormSection
								title="Informações do documento"
								description="Preencha com as informações necessárias para elaboração do contrato."
							/>
							<FormFactory form={templateVariablesForm} />
						</div>
					)}

					<div className={styles.footer}>
						{currentStep === 0 ? (
							<Button
								type="primary"
								onClick={handleNextStep}
								loading={isLoadingDemand}>
								{requireTemplateFields && !isUploadTemplate
									? 'Próxima etapa'
									: 'Cadastrar demanda'}
							</Button>
						) : (
							<div style={{ display: 'flex', gap: 8 }}>
								<Button onClick={handleBackNavigation}>Voltar</Button>
								<Button
									type="primary"
									onClick={onNext}
									loading={isLoadingDemand}>
									{isLastPage ? 'Cadastrar demanda' : 'Próxima etapa'}
								</Button>
							</div>
						)}
					</div>
				</StyledForm>
			</div>
		</div>
	)
}

export default CreateDemand
